import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSetUserActiveBranchMutation } from '@app/api/User/api';
import type { LeadCardBranchItemData } from '@app/components/LeadCard/data';
import { setRecordCreationContext } from '@app/components/RecordModal/model/recordModalSlice';
import { CALENDAR_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  setActiveBranchCountryId,
  setActiveBranchName,
  setUser,
  useAppDispatch,
} from '@app/store';
import { setActiveBranchSession } from '@app/utils';

import type { BaseState } from './useBaseState';

type UseLeadCardAddRecordParams = Pick<
  BaseState,
  | 'branches'
  | 'isBranchesLoading'
  | 'leadCardData'
  | 'selectedBranchId'
  | 'selectedLeadId'
>;

interface UseLeadCardAddRecordResult {
  isBranchSelectionModalOpen: boolean;
  branchSelectionOptions: LeadCardBranchItemData[];
  initialBranchSelectionId: number | null;
  isRecordBranchesLoading: boolean;
  openBranchSelectionModal(): void;
  closeBranchSelectionModal(): void;
  confirmBranchSelection(branchId: number): void;
}

const toNumericId = (id: number | string | null | undefined): number | null => {
  if (id === null || id === undefined) return null;

  const numericId = typeof id === 'number' ? id : Number(id);

  return Number.isFinite(numericId) ? numericId : null;
};

const getClientName = (
  nameFirst?: string | null,
  nameLast?: string | null
): string | null => {
  const clientName = `${nameLast ?? ''} ${nameFirst ?? ''}`.trim();

  return clientName || null;
};

export const useAddRecord = ({
  branches,
  isBranchesLoading,
  leadCardData,
  selectedBranchId,
  selectedLeadId,
}: UseLeadCardAddRecordParams): UseLeadCardAddRecordResult => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { activeBranchId, user } = useUserPrint();
  const [setUserActiveBranch] = useSetUserActiveBranchMutation();
  const {
    userAccessRights: {
      Records: { IsCreate },
    },
  } = useNewUserAccessRights();
  const { showNotification } = useNotification();

  const [isBranchSelectionModalOpen, setIsBranchSelectionModalOpen] =
    useState(false);

  const clientId = leadCardData?.ClientId ?? leadCardData?.ClientView.Id;
  const selectedBranchNumericId = toNumericId(selectedBranchId);

  useEffect(() => {
    setIsBranchSelectionModalOpen(false);
  }, [selectedLeadId]);

  const openBranchSelectionModal = useCallback(() => {
    if (!clientId) {
      showNotification('Идентификатор клиента не найден', 'frontWarning');
      return;
    }

    if (!IsCreate) return;

    if (branches.length === 0) {
      showNotification(
        'У клиента нет доступных филиалов для создания записи',
        'frontWarning'
      );
      return;
    }

    setIsBranchSelectionModalOpen(true);
  }, [branches.length, clientId, IsCreate, showNotification]);

  const closeBranchSelectionModal = useCallback(() => {
    setIsBranchSelectionModalOpen(false);
  }, []);

  const confirmBranchSelection = useCallback(
    async (recordBranchId: number) => {
      const numericBranchId = toNumericId(recordBranchId);

      if (numericBranchId === null) {
        showNotification('Филиал не был распознан', 'frontWarning');
        return;
      }

      if (!clientId) {
        showNotification('Идентификатор клиента не найден', 'frontWarning');
        return;
      }

      if (!IsCreate) {
        showNotification('Нет прав для данной операции', 'frontWarning');
        return;
      }

      setIsBranchSelectionModalOpen(false);

      // The appointment journal (ЖЗ) works on the active branch. If the chosen
      // branch differs, switch to it first so the journal shows that branch's
      // specialists and the record is created there.
      if (numericBranchId !== activeBranchId) {
        try {
          const { BranchId, CountryId, Branch } = await setUserActiveBranch({
            ActiveBranchId: activeBranchId,
            branchId: numericBranchId,
          }).unwrap();

          setActiveBranchSession({
            activeBranchId: BranchId,
            activeBranchName: Branch,
            activeBranchCountryId: CountryId,
          });
          dispatch(setUser({ ...user, activeBranchId: BranchId }));
          dispatch(setActiveBranchName(Branch));
          dispatch(setActiveBranchCountryId(CountryId));
        } catch (error) {
          showNotification(handleCatchError(error));
          return;
        }
      }

      // Carry the lead's client to the journal and open it. The record modal is
      // opened only from a journal slot click, which provides the specialist
      // (master) id — so the creation request is never sent without it.
      dispatch(
        setRecordCreationContext({
          clientId,
          clientName: getClientName(
            leadCardData?.ClientView.NameFirst,
            leadCardData?.ClientView.NameLast
          ),
        })
      );

      navigate(CALENDAR_PATH);
    },
    [
      activeBranchId,
      clientId,
      dispatch,
      IsCreate,
      leadCardData?.ClientView.NameFirst,
      leadCardData?.ClientView.NameLast,
      navigate,
      showNotification,
      user,
      setUserActiveBranch,
    ]
  );

  const initialBranchSelectionId = selectedBranchNumericId;

  return useMemo(
    () => ({
      isBranchSelectionModalOpen,
      initialBranchSelectionId,
      isRecordBranchesLoading: isBranchesLoading,
      openBranchSelectionModal,
      closeBranchSelectionModal,
      confirmBranchSelection,
      branchSelectionOptions: branches,
    }),
    [
      branches,
      closeBranchSelectionModal,
      confirmBranchSelection,
      initialBranchSelectionId,
      isBranchesLoading,
      isBranchSelectionModalOpen,
      openBranchSelectionModal,
    ]
  );
};
